import React, {memo, useState, useEffect, createRef} from 'react';
import { useHistory } from 'react-router-dom';
import {useProjectData} from '../contexts/ProjectDataContextProvider';
import {useFilterContext, useFilterDispatch} from '../contexts/FilterContextProvider';
import {updateHistory} from '../utils/data';

const searchShadowText = "Search by keyword";

function AutocompleteSearch({filtersOpen, setFiltersOpen}) {
    const history = useHistory();
    const {projectData} = useProjectData();
    const filters = useFilterContext();
    const filterDispatch = useFilterDispatch();
    let newFilters = JSON.parse(JSON.stringify(filters));
    const [searchState, setSearchState] = useState({
        activeSuggestion: 0,
        filteredSuggestions: [],
        showSuggestions: false,
        initUserInput: filters.search,
        userInput: filters.search ? filters.search : searchShadowText
    });

    useEffect(() => {
        setSearchState({
            activeSuggestion: 0,
            filteredSuggestions: [],
            showSuggestions: false,
            initUserInput: filters.search,
            userInput: filters.search ? filters.search : searchShadowText
        });
    }, [filters.search]);
    
    const suggestionsRef = createRef();

    const onChange = (e) => {
        let filteredSuggestions = [];
        if (!projectData.isLoading && !projectData.errorOccurred) {
            filteredSuggestions = projectData.projects.filter(suggestion =>
                (suggestion.FME_PROJ_NM && suggestion.FME_PROJ_NM.toLowerCase().indexOf(e.currentTarget.value.toLowerCase()) > -1) || 
                (suggestion.FME_PROJ_KEY_NO && suggestion.FME_PROJ_KEY_NO.toLowerCase().indexOf(e.currentTarget.value.toLowerCase()) > -1) ||
                (suggestion.FME_CITY && suggestion.FME_CITY.toLowerCase().indexOf(e.currentTarget.value.toLowerCase()) > -1) ||
                (suggestion.FME_COUNTY && suggestion.FME_COUNTY.toLowerCase().indexOf(e.currentTarget.value.toLowerCase()) > -1));
        }
        
        setSearchState({...searchState, 
            activeSuggestion: 0,
            filteredSuggestions,
            showSuggestions: true,
            initUserInput: e.currentTarget.value,
            userInput: e.currentTarget.value
        }); 
    };

    const onFocus = () => {
        if (searchState.userInput && searchState.userInput !== searchShadowText) {
            setSearchState({
                ...searchState, 
                showSuggestions: true
            }); 
        }
        if (searchState.userInput === searchShadowText) {
            setSearchState({
                ...searchState,
                userInput: ""
            });
        }
    };

    const onBlur = (e) => {
        // firefox onBlur issue workaround
        if (e.nativeEvent.explicitOriginalTarget &&
            e.nativeEvent.explicitOriginalTarget === e.nativeEvent.originalTarget) {
            return;
        }

        if (searchState.showSuggestions) {
            setTimeout(() => {
                setSearchState({...searchState, userInput: searchState.userInput === "" ? searchShadowText : searchState.userInput, activeSuggestion: 0, showSuggestions: false });
            }, 200);
        }

        if (searchState.userInput === "") {
            setSearchState({
                ...searchState,
                userInput: searchShadowText,
                activeSuggestion: 0,
                showSuggestions: false
            })
        }
    };

    const onKeyDown = e => {
        if (e.keyCode === 13) {
            processSearch(e, "");
        }
        else if (e.keyCode === 38) {
            if (searchState.activeSuggestion === 0) {
                return;
            }
            setSearchState({...searchState, activeSuggestion: searchState.activeSuggestion - 1, userInput: (searchState.activeSuggestion - 1 === 0) ? searchState.initUserInput : searchState.filteredSuggestions[searchState.activeSuggestion - 1].FME_PROJ_NM });
            if (suggestionsRef.current) {
                const activeElement = document.getElementsByClassName('suggestion-active')[0].previousSibling;
                if (activeElement) {
                    suggestionsRef.current.scrollTop = activeElement.offsetTop - 320;
                }
            }
        }
        else if (e.keyCode === 40) {
            if (searchState.activeSuggestion === searchState.filteredSuggestions.length) {
                return;
            }
            setSearchState({...searchState, activeSuggestion: searchState.activeSuggestion + 1, userInput: (searchState.activeSuggestion + 1 === 0) ? searchState.initUserInput : searchState.filteredSuggestions[searchState.activeSuggestion].FME_PROJ_NM });
            if (suggestionsRef.current) {
                const activeElement = document.getElementsByClassName('suggestion-active')[0].nextSibling;
                if (activeElement) {
                    suggestionsRef.current.scrollTop = activeElement.offsetTop - 320;
                }
            }
        }
    };

    const onClick = (e, suggestion) => {
        processSearch(e, suggestion);
    };

    const processSearch = (e, suggestion) => {
        let clickEvent = e.type == "click";
        setSearchState({
            ...searchState,
            aciveSuggestion: 0,
            showSuggestions: false,
            filteredSuggestions: clickEvent ? [] : searchState.filteredSuggestions,
            initUserInput: clickEvent ? searchState.initUserInput : searchState.activeSuggestion === 0 ? searchState.userInput : searchState.filteredSuggestions[searchState.activeSuggestion - 1].FME_PROJ_NM,
            userInput: clickEvent ? suggestion : searchState.activeSuggestion === 0 ? searchState.userInput : searchState.filteredSuggestions[searchState.activeSuggestion - 1].FME_PROJ_NM
        });
        
        newFilters.page = "projects";
        newFilters.search = clickEvent ? suggestion : searchState.activeSuggestion === 0 ? searchState.userInput : searchState.filteredSuggestions[searchState.activeSuggestion - 1].FME_PROJ_NM;
        newFilters.mapPoint = "";
        
        filterDispatch({
            type: 'search',
            pagePayload: newFilters.page,
            payload: clickEvent ? suggestion : searchState.activeSuggestion === 0 ? searchState.userInput : searchState.filteredSuggestions[searchState.activeSuggestion - 1].FME_PROJ_NM
        });
        updateHistory(history, newFilters);
        if (filtersOpen) {
            setFiltersOpen(!filtersOpen);
        }
    };

    return (
        <div className="autocomplete" onBlur={onBlur}>
            <input aria-label="Search" className="form-control" type="text" onFocus={onFocus} onChange={(e) => onChange(e)} onKeyDown={onKeyDown} value={searchState.userInput} />
            {searchState.showSuggestions && searchState.userInput ? 
                <ul className="suggestions" ref={suggestionsRef}>
                    <li className={searchState.activeSuggestion === 0 ? 'suggestion-active' : null} onClick={(e) => onClick(e, searchState.userInput)}>{searchState.initUserInput + " - search"}</li>
                    {searchState.filteredSuggestions.map((suggestion, index) => {
                        return (
                            <li className={searchState.activeSuggestion === index + 1 ? 'suggestion-active' : null} key={suggestion.FME_PROJ_KEY_NO} onClick={(e) => onClick(e, suggestion.FME_PROJ_NM)}>
                                {suggestion.FME_PROJ_NM}
                            </li>
                        );
                    })}
                </ul>
                :
                ""
            }
        </div>
    );
};

export default memo(AutocompleteSearch);